/**
 * CreateProjectModal Component
 * Modal form for creating a new project
 */

import { useState, useRef, useEffect } from 'react';
import { X, FolderGit2, Server, Package, Library, Check, Github, Search, Lock, Globe } from 'lucide-react';
import type { CreateProjectRequest, ProjectType, PaneType } from '../../api/projects';
import { useGitHubStatus, useGitHubRepos, type GitHubRepo } from '../../api/workspace-github';

const paneOptions: { value: PaneType; label: string; description: string }[] = [
  { value: 'development', label: 'Product', description: 'Development & Engineering' },
  { value: 'gtm', label: 'GTM', description: 'Content & Marketing' },
  { value: 'success', label: 'Success', description: 'Customer Success' },
];

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
  workspaceId: string;
}

const projectTypes: { value: ProjectType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'service',
    label: 'Service',
    description: 'Backend API or microservice',
    icon: <Server size={18} />,
  },
  {
    value: 'app',
    label: 'Application',
    description: 'Full-stack or frontend application',
    icon: <Package size={18} />,
  },
  {
    value: 'library',
    label: 'Library',
    description: 'Shared package or library',
    icon: <Library size={18} />,
  },
];

export function CreateProjectModal({ isOpen, onClose, onSubmit, workspaceId }: CreateProjectModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    issuePrefix: '',
    type: 'service' as ProjectType,
    panes: ['development'] as PaneType[],
    repoUrl: '',
    repoBranch: 'main',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [prefixTouched, setPrefixTouched] = useState(false);

  // GitHub repo picker state
  const [showRepoPicker, setShowRepoPicker] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const repoPickerRef = useRef<HTMLDivElement>(null);

  const { data: ghStatus } = useGitHubStatus(workspaceId);
  const isGitHubConnected = ghStatus?.connected ?? false;

  const { data: reposData, isLoading: loadingRepos } = useGitHubRepos(
    isGitHubConnected && showRepoPicker ? workspaceId : undefined,
    { search: repoSearch }
  );

  // Close picker on click outside
  useEffect(() => {
    if (!showRepoPicker) return;
    const handler = (e: MouseEvent) => {
      if (repoPickerRef.current && !repoPickerRef.current.contains(e.target as Node)) {
        setShowRepoPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRepoPicker]);

  const handleSelectRepo = (repo: GitHubRepo) => {
    setFormData(prev => ({ ...prev, repoUrl: repo.html_url, repoBranch: repo.default_branch }));
    setShowRepoPicker(false);
  };

  if (!isOpen) return null;

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const generatePrefix = (name: string): string => {
    const stripped = name.replace(/[-_]/g, ' ');
    const words = stripped.split(/\s+/).filter(w => w.length > 0);
    const skip = new Set(['the', 'a', 'an', 'of', 'for', 'and', 'v2', 'v3', 'v4', 'v5']);
    const meaningful = words.filter(w => !skip.has(w.toLowerCase()));
    const source = meaningful.length > 0 ? meaningful : words;
    if (source.length === 0) return '';
    if (source.length === 1) return source[0].slice(0, 4).toUpperCase();
    return source.slice(0, 4).map(w => w[0]).join('').toUpperCase();
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: slugTouched ? prev.slug : generateSlug(name),
      issuePrefix: prefixTouched ? prev.issuePrefix : generatePrefix(name),
    }));
  };

  const togglePane = (pane: PaneType) => {
    setFormData(prev => {
      const hasPaneAlready = prev.panes.includes(pane);
      // Don't allow removing the last pane
      if (hasPaneAlready && prev.panes.length === 1) {
        return prev;
      }
      return {
        ...prev,
        panes: hasPaneAlready
          ? prev.panes.filter(p => p !== pane)
          : [...prev.panes, pane],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const request: CreateProjectRequest = {
        workspaceId,
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name),
        type: formData.type,
        panes: formData.panes,
        ...(formData.issuePrefix.trim() && { issuePrefix: formData.issuePrefix.trim().toUpperCase() }),
        repository: {
          url: formData.repoUrl.trim(),
          branch: formData.repoBranch.trim() || 'main',
        },
      };

      await onSubmit(request);

      // Reset form and close
      setFormData({
        name: '',
        slug: '',
        issuePrefix: '',
        type: 'service',
        panes: ['development'],
        repoUrl: '',
        repoBranch: 'main',
      });
      setSlugTouched(false);
      setPrefixTouched(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-project-modal-title"
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg pointer-events-auto animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-coral-100 to-coral-50 flex items-center justify-center text-coral-600">
                <FolderGit2 size={20} />
              </div>
              <div>
                <h2 id="create-project-modal-title" className="text-lg font-semibold text-neutral-900">Create a new project</h2>
                <p className="text-sm text-neutral-500">Set up a new project in your workspace</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              aria-label="Close dialog"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                {error}
              </div>
            )}

            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Project Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="My Awesome Project"
                className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-500 transition-colors"
                required
              />
            </div>

            {/* Project Slug */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => { setSlugTouched(true); setFormData(prev => ({ ...prev, slug: e.target.value })); }}
                placeholder="my-awesome-project"
                className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-500 transition-colors font-mono text-sm"
                required
              />
              <p className="mt-1 text-xs text-neutral-500">
                Used in URLs and file paths. Only lowercase letters, numbers, and hyphens.
              </p>
            </div>

            {/* Issue Prefix */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Issue Prefix
              </label>
              <input
                type="text"
                value={formData.issuePrefix}
                onChange={(e) => { setPrefixTouched(true); setFormData(prev => ({ ...prev, issuePrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })); }}
                placeholder="e.g. CALC"
                className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-500 transition-colors font-mono text-sm uppercase"
                maxLength={10}
              />
              <p className="mt-1 text-xs text-neutral-500">
                Used in issue IDs (e.g. {formData.issuePrefix || 'PROJ'}-001). Auto-generated from name.
              </p>
            </div>

            {/* Project Type */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Project Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                {projectTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      formData.type === type.value
                        ? 'border-coral-500 bg-coral-50 text-coral-700'
                        : 'border-neutral-200 hover:border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex justify-center mb-2">{type.icon}</div>
                    <div className="text-sm font-medium">{type.label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Repository (optional) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Repository <span className="text-neutral-400 font-normal">(optional)</span>
              </label>
              <div className="relative" ref={repoPickerRef}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.repoUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, repoUrl: e.target.value }))}
                    placeholder="https://github.com/org/repo"
                    className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-500 transition-colors font-mono text-sm"
                  />
                  {isGitHubConnected && (
                    <button
                      type="button"
                      onClick={() => setShowRepoPicker(!showRepoPicker)}
                      className="px-2.5 py-2 border border-neutral-300 rounded-lg text-neutral-600 hover:bg-neutral-50 transition-colors flex items-center gap-1"
                    >
                      <Github size={14} />
                      <Search size={12} />
                    </button>
                  )}
                </div>

                {showRepoPicker && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-20 max-h-[280px] overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-neutral-100">
                      <input
                        type="text"
                        value={repoSearch}
                        onChange={(e) => setRepoSearch(e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-neutral-200 rounded text-[12px] focus:ring-1 focus:ring-coral-400 outline-none"
                        placeholder="Search repositories..."
                        autoFocus
                      />
                    </div>
                    <div className="overflow-y-auto flex-1 max-h-[200px]">
                      {loadingRepos ? (
                        <div className="px-3 py-4 text-center text-neutral-400 text-[12px]">Loading...</div>
                      ) : reposData?.repos?.length ? (
                        reposData.repos.map(repo => (
                          <button
                            key={repo.id}
                            type="button"
                            onClick={() => handleSelectRepo(repo)}
                            className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors border-b border-neutral-50 last:border-b-0"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] font-medium text-neutral-800">{repo.name}</span>
                              {repo.private ? <Lock size={10} className="text-neutral-400" /> : <Globe size={10} className="text-neutral-400" />}
                            </div>
                            {repo.description && (
                              <p className="text-[11px] text-neutral-400 mt-0.5 truncate">{repo.description}</p>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-center text-neutral-400 text-[12px]">No repos found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-neutral-500">
                {isGitHubConnected ? 'Pick from GitHub or paste a URL. Can configure later in settings.' : 'Paste a GitHub URL or configure later in project settings.'}
              </p>
            </div>

            {/* Pane Visibility */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Visible in Panes
              </label>
              <p className="text-xs text-neutral-500 mb-2">
                Choose which panes can see this project (at least one required)
              </p>
              <div className="grid grid-cols-3 gap-3">
                {paneOptions.map((pane) => {
                  const isSelected = formData.panes.includes(pane.value);
                  return (
                    <button
                      key={pane.value}
                      type="button"
                      onClick={() => togglePane(pane.value)}
                      className={`relative p-3 rounded-lg border text-center transition-all ${
                        isSelected
                          ? 'border-coral-500 bg-coral-50 text-coral-700'
                          : 'border-neutral-200 hover:border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-coral-500 flex items-center justify-center">
                          <Check size={10} className="text-white" />
                        </div>
                      )}
                      <div className="text-sm font-medium">{pane.label}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{pane.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2.5 text-sm font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Project'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
